/**
 * Rejestracja polskich ukladow w OpenLayers.
 *
 * Mapa jest rysowana NATYWNIE w ukladzie danych (PL-2000, strefy 5-8 albo
 * PL-1992) - bez reprojekcji do Web Mercatora. Dlatego kazdy uklad, ktory moze
 * wystapic w GML-u, musi byc znany proj4 zanim powstanie widok mapy.
 */
import proj4 from 'proj4'
import { register } from 'ol/proj/proj4'
import { get as getProjection } from 'ol/proj'

const DEFS: Record<number, string> = {
  // PL-1992
  2180: '+proj=tmerc +lat_0=0 +lon_0=19 +k=0.9993 +x_0=500000 +y_0=-5300000 '
      + '+ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  // PL-2000, strefy 5/6/7/8
  2176: '+proj=tmerc +lat_0=0 +lon_0=15 +k=0.999923 +x_0=5500000 +y_0=0 '
      + '+ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  2177: '+proj=tmerc +lat_0=0 +lon_0=18 +k=0.999923 +x_0=6500000 +y_0=0 '
      + '+ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  2178: '+proj=tmerc +lat_0=0 +lon_0=21 +k=0.999923 +x_0=7500000 +y_0=0 '
      + '+ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  2179: '+proj=tmerc +lat_0=0 +lon_0=24 +k=0.999923 +x_0=8500000 +y_0=0 '
      + '+ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
}

/**
 * Zakres Polski w stopniach - z niego liczymy zasieg kazdego ukladu.
 *
 * Zasieg jest potrzebny podkladom rastrowym w innym ukladzie niz mapa (OSM
 * siedzi w Web Mercatorze). OpenLayers buduje dla nich siatke kafli w ukladzie
 * DOCELOWYM, a gdy uklad nie zna swojego zasiegu, probuje go wyliczyc,
 * przeksztalcajac caly swiat od -180 do 180 stopni. Dla odwzorowania
 * poprzecznego Merkatora obcietego do jednej strefy wychodza z tego liczby bez
 * sensu i siatka kafli sie sypie.
 */
const POLSKA: [number, number, number, number] = [14.0, 48.9, 24.2, 55.0]

/** Zasieg ukladu policzony z siatki punktow po obszarze Polski. */
function extentOf(code: string): [number, number, number, number] {
  const [w, s, e, n] = POLSKA
  let xmin = Infinity, ymin = Infinity, xmax = -Infinity, ymax = -Infinity
  // Same rogi nie wystarcza: poludniki i rownolezniki przechodza w krzywe,
  // wiec skrajne wartosci potrafia lezec w polowie boku.
  for (let i = 0; i <= 8; i++) {
    for (let j = 0; j <= 8; j++) {
      const [x, y] = proj4('EPSG:4326', code,
                           [w + (e - w) * i / 8, s + (n - s) * j / 8])
      xmin = Math.min(xmin, x); xmax = Math.max(xmax, x)
      ymin = Math.min(ymin, y); ymax = Math.max(ymax, y)
    }
  }
  return [xmin, ymin, xmax, ymax]
}

let registered = false

export function ensureProjections() {
  if (registered) return
  for (const [code, def] of Object.entries(DEFS)) proj4.defs(`EPSG:${code}`, def)
  register(proj4)
  for (const code of Object.keys(DEFS)) {
    getProjection(`EPSG:${code}`)?.setExtent(extentOf(`EPSG:${code}`))
  }
  registered = true
}

export function projectionFor(srid: number) {
  ensureProjections()
  const p = getProjection(`EPSG:${srid}`)
  if (!p) throw new Error(`układ EPSG:${srid} nie jest zarejestrowany`)
  // Jednostek nie ustawiamy recznie - biora sie z '+units=m' w definicji proj4,
  // a to one decyduja o poprawnej podzialce i mianowniku skali.
  return p
}
